import { CONFIG } from '@/config/Config';
import Cookies from 'js-cookie';
import axios from 'axios';
import { addSessionId } from '@/core/utils/httpHeaders';

export interface ScheduledCarouselItem {
  image_url?: string;
  video_url?: string;
  cover_url?: string;
}

export interface ScheduledContent {
  _id: string;
  scheduled_to: Date;
  page_id: string;
  status: string;
  caption?: string;
  media_type: InstagramMediaType;
  carousel_items?: ScheduledCarouselItem[];
  /** Optional media metadata; legacy scheduled posts may omit raw_data or all usable media URLs. */
  raw_data?: {
    caption?: string;
    image_url?: string;
    cover_url?: string;
    video_url?: string;
  };
}

export interface CancelScheduledResponse {
  document_id: string;
  description: string;
  removed_from_deckard: boolean;
  removed_from_storage: boolean;
  canceled_from_mongo: boolean;
}

export enum InstagramMediaType {
  IMAGE = 'IMAGE',
  CAROUSEL = 'CAROUSEL',
  REELS = 'REELS',
  STORIES = 'STORIES',
}

export enum ScheduledPostStatus {
  SCHEDULED = 'SCHEDULED',
  PUBLISHED = 'PUBLISHED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  RETRYING = 'RETRYING',
}

export enum OrderBy {
  ASC = 'ASC',
  DESC = 'DESC',
}

export async function fetchScheduledContent(
  channel: string,
  statuses: ScheduledPostStatus[],
  pageIds: string[],
  skip: number,
  limit: number,
  orderBy: OrderBy,
): Promise<ScheduledContent[]> {
  try {
    const accId = CONFIG.cookieOrParam('acc_id')?.split('|')[0];
    const headers = addSessionId({
      accept: 'application/json',
    });

    const response = await axios.get(`${CONFIG.getServiceUrl('sac-reply')}/${channel}/scheduledcontent/${accId}/list`, {
      headers,
      params: {
        pageIds: pageIds.join(','),
        statuses: statuses.map(status => status.toString()).join(','),
        skip,
        limit,
        orderBy,
        projection: '_id,scheduled_to,status,caption,media_type,page_id,carousel_items',
      },
    });

    // Convert scheduled_to strings to Date objects
    return response.data.map((item: any) => ({
      ...item,
      scheduled_to: new Date(item.scheduled_to),
    }));
  } catch (error) {
    console.error('Error fetching active accounts:', error);
    throw error;
  }
}

export async function cancelScheduledContent(
  channel: string,
  pageId: string,
  scheduledContentId: string
): Promise<CancelScheduledResponse> {
  try {
    const accId = CONFIG.cookieOrParam('acc_id')?.split('|')[0];
    const headers = addSessionId({
      'Content-Type': 'application/json',
    });
    const token = Cookies.get('stilingue-session-id');

    if (!accId || !token) {
      throw new Error('Necessary tokens not Found');
    }
    headers['stilingue-session-id'] = token;

    const url = `${CONFIG.getServiceUrl('sac-reply')}/${channel}/scheduledcontent/${accId}/${pageId}/cancel-scheduled/${scheduledContentId}`;
    const response = await axios.delete<CancelScheduledResponse>(url, { 
        headers, 
        data: {}
    });
    return response.data;
  } catch (error) {
    console.error('Error canceling scheduled content:', error);
    throw error;
  }
}
